import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ChainId } from '@nominex/nomiswap-sdk'
import { analyticsApi } from 'restApi'
import { useWagmi } from 'libs/wagmi'
import { FetchStatus, State } from 'state/types'
import {
  getAmplitudeDeviceId,
  logAmplitudeEvent,
  logGtmEvent,
  setAmplitudeProp,
  setAmplitudePropOnce,
  setAmplitudeUserId,
} from 'utils/analytics'
import { useTotalInvestedUsd } from './useTotalInvestedUsd'

const useAnalytics = () => {
  // Handle wallet connect
  const { account, chainId, connector } = useWagmi()
  // Handle user assets in farms and pools
  const { totalUsdInAllFarms, totalUsdInAllPools, userDataLoaded } = useTotalInvestedUsd()
  const [totalFarms, setTotalFarms] = useState(0)
  const [totalInvested, setTotalInvested] = useState(0)

  useEffect(() => {
    if (userDataLoaded) {
      const significantUsdInAllFarms = Math.ceil(totalUsdInAllFarms * 100) / 100
      const significantUsdInAllPools = Math.ceil(totalUsdInAllPools * 100) / 100

      if (totalFarms !== significantUsdInAllFarms) {
        setTotalFarms(significantUsdInAllFarms)
        if (chainId === ChainId.BSC) {
          setAmplitudeProp('defi_total_staked_in_usdt', totalFarms)
        } else {
          setAmplitudeProp(`defi_total_staked_in_usdt_chain_${chainId}`, totalFarms)
        }
      }

      if (totalInvested !== significantUsdInAllFarms + significantUsdInAllPools) {
        setTotalInvested(significantUsdInAllFarms + significantUsdInAllPools)
        if (chainId === ChainId.BSC) {
          setAmplitudeProp('defi_total_invested_in_usdt', totalInvested)
        } else {
          setAmplitudeProp(`defi_total_invested_in_usdt_chain_${chainId}`, totalInvested)
        }
      }
    }
  }, [chainId, totalFarms, totalInvested, totalUsdInAllFarms, totalUsdInAllPools, userDataLoaded])

  // Handle user farming level
  const { currentLevel, fetchStatus } = useSelector((state: State) => state.affiliate.user.farmingLevel)

  useEffect(() => {
    if (fetchStatus === FetchStatus.FETCHED) {
      setAmplitudeProp('affiliate_package', currentLevel)
    }
  }, [fetchStatus, currentLevel])

  // Handle userId and referredBy
  const { userId, referredBy } = useSelector((state: State) => state.profile.accountInfo)

  useEffect(() => {
    if (userId) {
      setAmplitudeUserId(userId)
    }
  }, [userId])
  useEffect(() => {
    if (referredBy) {
      setAmplitudeProp('referred_by', referredBy)
    }
  }, [referredBy])

  useEffect(() => {
    if (account) {
      const deviceId = getAmplitudeDeviceId()

      setAmplitudeProp('defi_wallet_type', connector?.name)
      setAmplitudePropOnce('defi_wallet_connected', true)

      logAmplitudeEvent('DefiWalletConnected', { wallet_type: connector?.name, website: 'dex' })
      logGtmEvent({ event: 'DefiWalletConnected' })

      analyticsApi.notifyWalletConnect({ notifyWalletConnectRequest: { deviceId, walletAddress: account } })
    }
  }, [account, connector?.name])
}

export default useAnalytics
